import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// Ui components
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { GenericFilter } from "@ui-components/Filter/GenericFilter"; // <-- Filtro

// Llamadas de syslog
import {
    getCTXRlistGlobal,
    getAvailableSyslogs,
    getSelectedSyslogs,
    saveSyslogs,
    SyslogItem,
} from "./request/request";

// Columnas
import {
    getAvailableColumns,
    getSelectedColumns,
    LOG_LEVELS,
} from "./SyslogData";

interface CtxrItem {
    id: string;
    name: string;
    description: string;
    subscriptionId: number;
}

function SyslogPage() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const [isFetching, setIsFetching] = useState(false);

    // Lista CTX-R
    const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
    const [instanceOptions, setInstanceOptions] = useState<string[]>([]);
    const [selectedInstance, setSelectedInstance] = useState("");

    // Syslog items
    const [availableItems, setAvailableItems] = useState<SyslogItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<SyslogItem[]>([]);

    // Copia inicial (Undo)
    const [initialAvailable, setInitialAvailable] = useState<SyslogItem[]>([]);
    const [initialSelected, setInitialSelected] = useState<SyslogItem[]>([]);

    // Para checkboxes
    const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(new Set());
    const [selectedSelectedIds, setSelectedSelectedIds] = useState<Set<number>>(new Set());

    // Detectar cambios => Undo / Save
    const [hasChanges, setHasChanges] = useState(false);

    // Filtros Available
    const [searchTermAvail, setSearchTermAvail] = useState("");
    const [searchColumnAvail, setSearchColumnAvail] = useState("All");

    // Filtros Selected
    const [searchTermSel, setSearchTermSel] = useState("");
    const [searchColumnSel, setSearchColumnSel] = useState("All");

    // Opciones de columnas
    const searchOptions = ["All", "IP", "Name"];

    function filterSyslogs(
        list: SyslogItem[],
        searchTerm: string,
        searchColumn: string
    ): SyslogItem[] {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return list;

        return list.filter((item) => {
            const ipLower = item.ip.toLowerCase();
            const nameLower = (item.description ?? "").toLowerCase();

            switch (searchColumn) {
                case "IP":
                    return ipLower.includes(term);
                case "Name":
                    return nameLower.includes(term);
                case "All":
                default:
                    return ipLower.includes(term) || nameLower.includes(term);
            }
        });
    }

    // Filtrados
    const filteredAvailable = filterSyslogs(availableItems, searchTermAvail, searchColumnAvail);
    const filteredSelected = filterSyslogs(selectedItems, searchTermSel, searchColumnSel);

    // Revisar cambios
    useEffect(() => {
        const sameAvail = JSON.stringify(availableItems) === JSON.stringify(initialAvailable);
        const sameSel = JSON.stringify(selectedItems) === JSON.stringify(initialSelected);
        setHasChanges(!sameAvail || !sameSel);
    }, [availableItems, selectedItems, initialAvailable, initialSelected]);

    // Montar => cargar CTX-R
    useEffect(() => {
        if (isLoading || !isAuthenticated) return;

        const fetchCTXRList = async () => {
            try {
                setIsFetching(true);
                const token = await getAccessTokenSilently();
                const response = await getCTXRlistGlobal(-1, 1, token);
                const dataArray: CtxrItem[] = response.data || [];

                setFullCTXRlist(dataArray);
                const mapped = dataArray.map((item) => `${item.name} (${item.description})`);
                setInstanceOptions(mapped);

                if (mapped.length > 0) {
                    const first = mapped[0];
                    setSelectedInstance(first);
                    const found = dataArray.find(
                        (x) => `${x.name} (${x.description})` === first
                    );
                    if (found) {
                        await loadSyslogData(found.id);
                    }
                }
            } catch (err) {
                console.error("Error fetching CTXR list:", err);
            } finally {
                setIsFetching(false);
            }
        };

        fetchCTXRList();
    }, [isLoading, isAuthenticated, getAccessTokenSilently]);

    const handleInstanceSelect = async (instanceStr: string) => {
        setSelectedInstance(instanceStr);
        const found = fullCTXRlist.find(
            (x) => `${x.name} (${x.description})` === instanceStr
        );
        if (found) {
            await loadSyslogData(found.id);
        }
    };

    const handleRefresh = async () => {
        const found = fullCTXRlist.find(
            (x) => `${x.name} (${x.description})` === selectedInstance
        );
        if (!found) return;
        await loadSyslogData(found.id);
    };

    const loadSyslogData = async (ctxrId: string) => {
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();

            const subscriptionId = 1;
            const tenantId = 1;

            // Available
            const respAvail = await getAvailableSyslogs(subscriptionId, ctxrId, tenantId, token);
            const dataAvail = respAvail.data as SyslogItem[];

            // Selected
            const respSelected = await getSelectedSyslogs(subscriptionId, ctxrId, tenantId, token);
            const dataSelected = respSelected.data as SyslogItem[];

            setAvailableItems(dataAvail);
            setSelectedItems(dataSelected);

            setInitialAvailable(JSON.parse(JSON.stringify(dataAvail)));
            setInitialSelected(JSON.parse(JSON.stringify(dataSelected)));

            // Limpiar sets
            setSelectedAvailableIds(new Set());
            setSelectedSelectedIds(new Set());
        } catch (error) {
            console.error("Error loading syslog data =>", error);
        } finally {
            setIsFetching(false);
        }
    };

    // Toggle check
    const toggleAvailableId = (id: number) => {
        setSelectedAvailableIds((prev) => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    };

    const toggleSelectedId = (id: number) => {
        setSelectedSelectedIds((prev) => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    };

    // Mover flechas
    const handleMoveToSelected = () => {
        if (selectedAvailableIds.size === 0) return;
        const moving = availableItems.filter((it) => selectedAvailableIds.has(it.id));
        const remainAvail = availableItems.filter((it) => !selectedAvailableIds.has(it.id));
        const mergedSelected = [...selectedItems, ...moving];

        setAvailableItems(remainAvail);
        setSelectedItems(mergedSelected);
        setSelectedAvailableIds(new Set());
    };

    const handleMoveToAvailable = () => {
        if (selectedSelectedIds.size === 0) return;
        const moving = selectedItems.filter((it) => selectedSelectedIds.has(it.id));
        const remainSelected = selectedItems.filter((it) => !selectedSelectedIds.has(it.id));
        const newAvailable = [...availableItems, ...moving];

        setAvailableItems(newAvailable);
        setSelectedItems(remainSelected);
        setSelectedSelectedIds(new Set());
    };

    // Cambio de Level
    const handleLevelChange = (row: SyslogItem, newLevelStr: string) => {
        const newLevelIndex = LOG_LEVELS.indexOf(newLevelStr);
        setSelectedItems((prev) =>
            prev.map((it) =>
                it.id === row.id ? { ...it, level: newLevelIndex } : it
            )
        );
    };

    // Alerts
    const [undoAlertOpen, setUndoAlertOpen] = useState(false);

    const doActualUndo = () => {
        setAvailableItems(JSON.parse(JSON.stringify(initialAvailable)));
        setSelectedItems(JSON.parse(JSON.stringify(initialSelected)));
        setSelectedAvailableIds(new Set());
        setSelectedSelectedIds(new Set());
    };

    const handleUndo = () => {
        if (!hasChanges) return;
        setUndoAlertOpen(true);
    };

    const confirmUndo = () => {
        setUndoAlertOpen(false);
        doActualUndo();
        setSuccessAlertOpen(true);
        setAlertTitle("Discarded");
        setAlertDesc("All changes have been discarded.");
    };

    const cancelUndo = () => {
        setUndoAlertOpen(false);
    };

    // Save
    const [saveAlertOpen, setSaveAlertOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = () => {
        if (!hasChanges) return;
        setSaveAlertOpen(true);
    };

    const confirmSave = async () => {
        setSaveAlertOpen(false);
        setIsSaving(true);

        try {
            const found = fullCTXRlist.find(
                (x) => `${x.name} (${x.description})` === selectedInstance
            );
            if (!found) throw new Error("No CTX-R found to save.");

            const token = await getAccessTokenSilently();
            const subscriptionId = 1;
            const tenantId = 1;

            await saveSyslogs(subscriptionId, found.id, tenantId, token, selectedItems);
            await loadSyslogData(found.id);

            // Exito => success alert
            setSuccessAlertOpen(true);
            setAlertTitle("Saved!");
            setAlertDesc("Your changes have been saved successfully.");
        } catch (err) {
            console.error("Error saving syslogs =>", err);
            setErrorAlertOpen(true);
            setErrorTitle("Error on save");
            setErrorDesc("Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const cancelSave = () => {
        setSaveAlertOpen(false);
    };

    // Success alert
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertDesc, setAlertDesc] = useState("");

    const closeSuccessAlert = () => {
        setSuccessAlertOpen(false);
    };

    // Error alert
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorTitle, setErrorTitle] = useState("");
    const [errorDesc, setErrorDesc] = useState("");

    const closeErrorAlert = () => {
        setErrorAlertOpen(false);
    };

    // Columnas
    const availableColumns = getAvailableColumns(selectedAvailableIds, toggleAvailableId);
    const selectedColumns = getSelectedColumns(selectedSelectedIds, toggleSelectedId, handleLevelChange);

    return (
        <div className="relative">
            {/* HEADER */}
            <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
                <div className="flex items-end justify-between">
                    <div className="flex items-end space-x-4">
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                                CTX-R instance
                            </label>
                            <GenericDropdown
                                items={instanceOptions}
                                value={selectedInstance}
                                onSelect={handleInstanceSelect}
                                minWidthClass="min-w-[300px]"
                                widthClass="w-60"
                            />
                        </div>

                        <GenericButton
                            label=""
                            iconLeftName="refresh"
                            variant="secondary"
                            shape="ghost"
                            size="md"
                            onClick={handleRefresh}
                        />
                    </div>
                </div>
            </div>

            {/* CONTENIDO: 2 tablas + Filtros */}
            <div className="flex gap-6">
                {/* LADO IZQ: Filtro + Available */}
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-semantic-text-primary font-medium">Available</h3>
                        {/* Filtro a la derecha */}
                        <GenericFilter
                            searchColumn={searchColumnAvail}
                            onSearchColumnChange={setSearchColumnAvail}
                            searchTerm={searchTermAvail}
                            onSearchTermChange={setSearchTermAvail}
                            columnOptions={searchOptions}
                        />
                    </div>

                    <GenericTable
                        columns={availableColumns}
                        data={filteredAvailable}
                        initialRowsPerPage={5}
                        rowsPerPageOptions={[5, 10]}
                        tableClassName="bg-semantic-bg-primary"
                        width="100%"
                        rowKey={(row) => row.id}
                        onRowClick={(row) => toggleAvailableId(row.id)}
                        selectedRowKeys={new Set(selectedAvailableIds)}
                    />
                </div>

                {/* Flechas */}
                <div className="flex flex-col items-center justify-center gap-4">
                    <GenericButton
                        label=""
                        iconLeftName="arrowRight"
                        variant="secondary"
                        shape="ghost"
                        size="md"
                        onClick={handleMoveToSelected}
                        // DESHABILITADO si no hay ningún ítem seleccionado en Available
                        disabled={selectedAvailableIds.size === 0}
                    />
                    <GenericButton
                        label=""
                        iconLeftName="arrowLeft"
                        variant="secondary"
                        shape="ghost"
                        size="md"
                        onClick={handleMoveToAvailable}
                        // DESHABILITADO si no hay ningún ítem seleccionado en Selected
                        disabled={selectedSelectedIds.size === 0}
                    />
                </div>

                {/* LADO DER: Filtro + Selected */}
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-semantic-text-primary font-medium">Selected</h3>
                        {/* Filtro a la derecha */}
                        <GenericFilter
                            searchColumn={searchColumnSel}
                            onSearchColumnChange={setSearchColumnSel}
                            searchTerm={searchTermSel}
                            onSearchTermChange={setSearchTermSel}
                            columnOptions={searchOptions}
                        />
                    </div>

                    <GenericTable
                        columns={selectedColumns}
                        data={filteredSelected}
                        initialRowsPerPage={5}
                        rowsPerPageOptions={[5, 10]}
                        tableClassName="bg-semantic-bg-primary"
                        width="100%"
                        rowKey={(row) => row.id}
                        onRowClick={(row) => toggleSelectedId(row.id)}
                        selectedRowKeys={new Set(selectedSelectedIds)}
                    />
                </div>
            </div>

            {/* BOTONES: Undo & Save */}
            <div className="mt-4 flex items-center justify-end gap-4">
                <GenericButton
                    label="Undo"
                    variant="secondary"
                    size="md"
                    onClick={handleUndo}
                    disabled={!hasChanges}
                />
                <GenericButton
                    label={isSaving ? "Saving..." : "Save"}
                    variant="brand"
                    size="md"
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                />
            </div>

            {/* Overlay Spinner */}
            {isFetching && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
                    <h2 className="text-xl font-semibold mb-4 text-white">
                        Processing request...
                    </h2>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
                </div>
            )}

            {/* ALERTS */}
            {/* 1) Confirm Undo */}
            <GenericAlert
                isOpen={undoAlertOpen}
                onClose={cancelUndo}
                onConfirm={confirmUndo}
                title="Unsaved Changes"
                description="Are you sure you want to discard the changes?"
                cancelLabel="No"
                confirmLabel="Yes"
                variant="confirm"
            />

            {/* 2) Confirm Save */}
            <GenericAlert
                isOpen={saveAlertOpen}
                onClose={cancelSave}
                onConfirm={confirmSave}
                title="Save Changes"
                description="Are you sure you want to save the changes?"
                cancelLabel="No"
                confirmLabel="Yes"
                variant="confirm"
            />

            {/* 3) Success Alert => se cierra con “Close” */}
            <GenericAlert
                isOpen={successAlertOpen}
                onClose={closeSuccessAlert}
                onConfirm={closeSuccessAlert}
                title={alertTitle}
                description={alertDesc}
                cancelLabel="Close"
                confirmLabel="OK"
                variant="success"
            />

            {/* 4) Error Alert */}
            <GenericAlert
                isOpen={errorAlertOpen}
                onClose={closeErrorAlert}
                onConfirm={closeErrorAlert}
                title={errorTitle}
                description={errorDesc}
                cancelLabel="Close"
                confirmLabel="OK"
                variant="error"
            />
        </div>
    );
}

export default SyslogPage;
